/**
 * B2B B2B Review Order JavaScript
 *
 * Enterprise B2B order review page with:
 * - Cart data display (read-only)
 * - Pagination
 * - Order summary with totals
 * - Submit Order functionality
 */

(function () {
  'use strict';

  const DRAFT_ORDER_SESSION_KEYS = {
    DRAFT_ORDER_ID: '_draft_order_id',
    CHECKOUT_URL: '_draft_order_checkout_url',
    CONTEXT_HASH: '_draft_order_context_hash',
    CART_CURRENCY: '_cart_currency',
    CART_FINGERPRINT: '_draft_order_cart_fingerprint',
  };

  const B2BReview = {
    cart: null,
    checkoutData: null,
    currentPage: 1,
    rowsPerPage: 10,

    // B2B context for Draft Order checkout
    b2bContext: null,

    // DOM Elements
    elements: {},

    /**
     * Initialize the review page
     */
    async init() {
      this.strings = window.B2B_REVIEW_CONFIG && window.B2B_REVIEW_CONFIG.strings ? window.B2B_REVIEW_CONFIG.strings : {};
      this.cacheElements();
      this.loadCheckoutData();
      await this.fetchCart();
      this.bindEvents();
      this.setTimestamp();
      this.initMobileDropdown();
    },

    /**
     * Mobile: Start "Your Order" section collapsed; desktop: keep open
     */
    initMobileDropdown() {
      const details = document.getElementById('b2b-review-main-details');
      if (!details) return;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (isMobile) {
        details.removeAttribute('open');
      } else {
        details.setAttribute('open', '');
      }
    },

    /**
     * Load checkout data from sessionStorage
     */
    loadCheckoutData() {
      try {
        const data = sessionStorage.getItem('b2b_checkout_data');
        if (data) {
          this.checkoutData = JSON.parse(data);
        }
      } catch (error) {
        this.checkoutData = null;
      }

      // Fetch B2B context if customer is logged in (uses JWT + context cookie)
      const config = window.B2B_REVIEW_CONFIG || {};
      if (config.customerId) {
        this.fetchB2BContext();
      }
    },

    /**
     * Reconstruct the shipping address from cart attributes when b2b_checkout_data is
     * missing or incomplete. b2b-checkout.js saveCartAttributes() persists the chosen
     * address to the cart (_checkout_custom_shipping_address_json for "new"/"addressbook",
     * flat _shipping_* keys once available), and the cart survives the location-picker
     * sessionStorage wipe and page reloads that sessionStorage does not.
     * Returns a shipping_address object shaped like collectFormData() output, or null.
     */
    restoreShippingAddressFromCartAttributes() {
      const a = this.cart?.attributes || {};

      // Preferred source: full JSON the checkout page wrote for "new"/"addressbook"
      if (a._checkout_custom_shipping_address_json) {
        try {
          const parsed = JSON.parse(a._checkout_custom_shipping_address_json);
          if (parsed && (parsed.address1 || parsed.city)) {
            return {
              type: parsed.type || 'addressbook',
              company: parsed.company || '',
              attention: parsed.attention || '',
              address1: parsed.address1 || '',
              address2: parsed.address2 || '',
              city: parsed.city || '',
              state: parsed.state || '',
              zip: parsed.zip || '',
              country: parsed.country || '',
              phone: parsed.phone || '',
              firstName: parsed.firstName || '',
              lastName: parsed.lastName || '',
              save_to_addressbook: Boolean(parsed.save_to_addressbook),
              ...(parsed.addressbook_id ? {addressbook_id: parsed.addressbook_id} : {}),
            };
          }
        } catch (error) {
          console.warn('[b2b-review-order] Invalid _checkout_custom_shipping_address_json, trying flat keys');
        }
      }

      // Secondary source: flat _shipping_* keys. Skip "current" — its flat keys hold the
      // company location address, which the default useCompanyLocationAddress path already
      // applies; restoring it here would mislabel the address type.
      if ((a._shipping_address_type === 'new' || a._shipping_address_type === 'addressbook') &&
          (a._shipping_address1 || a._shipping_city || a._shipping_zip)) {
        return {
          type: a._shipping_address_type,
          company: a._shipping_company || '',
          attention: a._attention || '',
          address1: a._shipping_address1 || '',
          address2: a._shipping_address2 || '',
          city: a._shipping_city || '',
          state: a._shipping_state || '',
          zip: a._shipping_zip || '',
          country: a._shipping_country || '',
          firstName: a._shipping_first_name || '',
          lastName: a._shipping_last_name || '',
          addressbook_id: a._addressbook_id || '',
        };
      }

      return null;
    },

    /**
     * Parse context cookie (company_id, location_id) - same pattern as b2b-checkout / rs-location-picker
     */
    getContextFromCookie() {
      const cookies = (document.cookie || '').split(';').reduce((acc, s) => {
        const i = s.indexOf('=');
        if (i === -1) return acc;
        acc[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(s.slice(i + 1).trim());
        return acc;
      }, {});
      try {
        return cookies.context ? JSON.parse(cookies.context) : null;
      } catch {
        return null;
      }
    },

    /**
     * Build context hash from company + location for draft order invalidation
     */
    getContextHash() {
      if (!this.b2bContext) return '';
      const companyId = this.b2bContext.companyId || '';
      const locationId = (this.b2bContext.locationId || '').replace('gid://shopify/CompanyLocation/', '');
      return `${companyId}:${locationId}`;
    },

    /**
     * Stable identifier for the current cart's contents (variant + quantity). A draft order is
     * treated like a cart: when this changes, the stored draft order belongs to a previous cart
     * and must not be updated.
     */
    getCartFingerprint() {
      const items = (this.cart && this.cart.items) || [];
      return items
        .map((item) => `${item.variant_id}:${item.quantity}`)
        .sort()
        .join('|');
    },

    /**
     * Get persisted draft order session state (for create vs update and invalidation)
     */
    getDraftOrderSessionState() {
      try {
        return {
          draftOrderId: sessionStorage.getItem(DRAFT_ORDER_SESSION_KEYS.DRAFT_ORDER_ID) || null,
          checkoutUrl: sessionStorage.getItem(DRAFT_ORDER_SESSION_KEYS.CHECKOUT_URL) || null,
          contextHash: sessionStorage.getItem(DRAFT_ORDER_SESSION_KEYS.CONTEXT_HASH) || null,
          cartCurrency: sessionStorage.getItem(DRAFT_ORDER_SESSION_KEYS.CART_CURRENCY) || null,
          cartFingerprint: sessionStorage.getItem(DRAFT_ORDER_SESSION_KEYS.CART_FINGERPRINT) || null,
        };
      } catch {
        return {draftOrderId: null, checkoutUrl: null, contextHash: null, cartCurrency: null, cartFingerprint: null};
      }
    },

    /**
     * Persist draft order session state after create/update
     */
    setDraftOrderSessionState(state) {
      try {
        if (state.draftOrderId != null) sessionStorage.setItem(DRAFT_ORDER_SESSION_KEYS.DRAFT_ORDER_ID, String(state.draftOrderId));
        if (state.checkoutUrl != null) sessionStorage.setItem(DRAFT_ORDER_SESSION_KEYS.CHECKOUT_URL, state.checkoutUrl);
        if (state.contextHash != null) sessionStorage.setItem(DRAFT_ORDER_SESSION_KEYS.CONTEXT_HASH, state.contextHash);
        if (state.cartCurrency != null) sessionStorage.setItem(DRAFT_ORDER_SESSION_KEYS.CART_CURRENCY, state.cartCurrency);
        if (state.cartFingerprint != null) sessionStorage.setItem(DRAFT_ORDER_SESSION_KEYS.CART_FINGERPRINT, state.cartFingerprint);
      } catch (e) {
        // sessionStorage full or unavailable
      }
    },

    /**
     * Clear cart, draft order session state, and all checkout session data.
     * Required for context integrity: run on any company or location change (custom selector,
     * OOTB location, or when returning from Shopify Checkout with different context).
     */
    async clearDraftOrderSessionState() {
      try {
        await fetch('/cart/clear.js', {method: 'POST'});
      } catch (e) {}
      this.clearDraftOrderSessionKeys();
      this.checkoutData = null;
      await this.fetchCart();
    },

    /**
     * Clear only the draft order sessionStorage keys (does NOT clear the cart).
     * Called before redirecting to Shopify checkout so that a retry creates a fresh
     * draft order instead of trying to update a stale one.
     */
    clearDraftOrderSessionKeys() {
      try {
        sessionStorage.removeItem(DRAFT_ORDER_SESSION_KEYS.DRAFT_ORDER_ID);
        sessionStorage.removeItem(DRAFT_ORDER_SESSION_KEYS.CHECKOUT_URL);
        sessionStorage.removeItem(DRAFT_ORDER_SESSION_KEYS.CONTEXT_HASH);
        sessionStorage.removeItem(DRAFT_ORDER_SESSION_KEYS.CART_CURRENCY);
        sessionStorage.removeItem(DRAFT_ORDER_SESSION_KEYS.CART_FINGERPRINT);
      } catch (e) {
        // sessionStorage unavailable
      }
    },

    /**
     * Set B2B context from context cookie.
     * Sets companyId, locationId for Draft Order checkout.
     */
    async fetchB2BContext(){
      const ctx = this.getContextFromCookie();
      this.b2bContext = {
        companyId: ctx.company_id,
        companyName: ctx.company_name,
        locationId: ctx.location_id,
        locationName: ctx.location_name || null,
        contactId: undefined,
      };
      this.enforceContextIntegrity();
    },

    /**
     * Context integrity: if stored draft order context (company+location) does not match current
     * context (e.g. user changed company/location in another tab, or inside Shopify Checkout),
     * clear cart, delete existing draft order, and reset all checkout session data.
     * Only runs when draft order checkout is enabled; when the feature flag is off we never
     * clear the cart so real-time pricing and the review page keep working.
     */
    async enforceContextIntegrity() {
      const config = window.B2B_REVIEW_CONFIG || {};
      if (!config.useDraftOrderCheckout) return;

      const sessionState = this.getDraftOrderSessionState();
      const currentHash = this.getContextHash();
      if (!currentHash || !sessionState.contextHash || sessionState.contextHash === currentHash) {
        return;
      }
      if (sessionState.draftOrderId && config.gadgetApiBase) {
        try {
          await fetch(`${config.gadgetApiBase}/api/draft-orders/${encodeURIComponent(sessionState.draftOrderId)}`, {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${window.myJWT}`},
          });
        } catch (e) {
          // Best effort; clear local state regardless
        }
      }
      await this.clearDraftOrderSessionState();
      this.checkoutData = null;
      await this.fetchCart();
    },

    /**
     * Create or update Draft Order (bridge between Order Review and Shopify Checkout).
     * Shopify Checkout is strictly payment-only; order details (shipping, billing, etc.) are set
     * here and must not be edited in Shopify Checkout.
     * Preserves B2B context (purchasingEntity) and user selections (customAttributes).
     * Always passes shipping address from Custom Checkout; never relies on Shopify defaults.
     * When draftOrderId exists and context hash matches, updates; otherwise creates.
     */
    async createDraftOrderCheckout() {
      const config = window.B2B_REVIEW_CONFIG || {};
      const checkoutData = this.checkoutData || {};

      if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
        return null;
      }

      if (!config.gadgetApiBase) {
        return null;
      }

      const sessionState = this.getDraftOrderSessionState();
      const currentContextHash = this.getContextHash();
      const currentCartFingerprint = this.getCartFingerprint();
      // A draft order belongs to the cart it was created from. If the stored fingerprint is
      // missing (state predates this check) or differs, the customer's cart has changed since the
      // draft order was created — treat it as a new cart and create a fresh draft order instead
      // of updating the stale one with different line items.
      const isUpdate = Boolean(
        sessionState.draftOrderId &&
        sessionState.contextHash &&
        sessionState.contextHash === currentContextHash &&
        sessionState.cartFingerprint &&
        sessionState.cartFingerprint === currentCartFingerprint
      );

      const shippingAddress = checkoutData.shipping_address || {};
      // No `|| shippingAddress` fallback: when billing is same-as-shipping the backend derives
      // the billing address from the company location's BILLING address. Aliasing shipping here
      // would leak shipping values into _billing_* cart attributes and a bogus requestBody.billingAddress.
      const billingAddress = checkoutData.billing_address || null;
      const shippingMethod = checkoutData.shipping_method || {};
      const paymentMethod = checkoutData.payment_method?.type || 'purchase_order';
      const poNumber = checkoutData.payment_method?.po_number || '';

      // Use the B2B company location's saved address only for "current" (the company's default
      // address). "addressbook" already carries a specific, complete address selected from the
      // customer's own saved addresses (see b2b-checkout.js collectFormData) — treating it the
      // same as "current" would discard that selection and substitute the company's default
      // location address instead. "new" (a freshly entered address) also has its own address data
      // and must not be overridden either.
      const shippingAddressType = shippingAddress.type || 'current';
      const useCompanyLocationAddress = Boolean(this.b2bContext) && shippingAddressType === 'current';

      // Display label for checkout shipping section: "FedEx Ground" or "Best way available"
      const shippingMethodDisplay = shippingMethod.type === 'best_way' || !shippingMethod.carrier ? 'Best way available' : [shippingMethod.carrier, shippingMethod.service].filter(Boolean).join(' ').trim() || 'Best way available';

      // Get ship complete from cart attributes (set on B2B cart page)
      const cartAttributes = this.cart?.attributes || {};
      const appliedSurchargeCents = this.getAppliedSurchargeCents();
      const shipComplete = cartAttributes.ship_complete === 'true' || shippingMethod.ship_complete === true;

      try {
        // Build discount codes array from cart (e.g. _promo_code comma-separated)
        const discountCodesRaw = cartAttributes._promo_code || '';
        const discountCodes = discountCodesRaw
          ? discountCodesRaw
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
          : [];

        const requestBody = {
          email: config.customerEmail,
          paymentMethod: paymentMethod,
          cartLines: this.cart.items.map((item) => {
            const unitCents = this.getRtpUnitCents(item);
            return {
              variantId: item.variant_id,
              quantity: item.quantity,
              ...(unitCents != null ? {unitPrice: (unitCents / 100).toFixed(2)} : {}),
              // Line properties are dropped at the draft order boundary unless passed explicitly;
              // the backend carries the requested delivery dates onto the draft order line items.
              properties: item.properties || {},
            };
          }),
          customerId: config.customerId,
          note: poNumber ? `PO Number: ${poNumber}` : this.cart.note || '',
          discountCodes: discountCodes.length ? discountCodes : undefined,
          shippingAmount: (appliedSurchargeCents / 100).toFixed(2),
          currency: this.cart?.currency || config.currency || undefined,
          // Carry EVERY cart attribute onto the draft order: cart attributes are dropped at the
          // draft-order boundary unless listed here, so the final order would otherwise be missing
          // keys the standard checkout path has (e.g. _surcharges, sales_area_data,
          // _billing_address_external_id, _checkout_custom_shipping_address_json). Start from the
          // full cart attribute set, then override with the explicit entries below where the
          // review page's form state is authoritative.
          customAttributes: (() => {
            const attrs = {};
            Object.keys(cartAttributes).forEach((key) => {
              const value = cartAttributes[key];
              attrs[key] = value != null ? String(value) : '';
            });
            const set = (key, value) => { attrs[key] = value != null ? String(value) : ''; };
            // The checkout extension shows a surcharge banner whenever `_surcharges` is non-empty,
            // so clear it when the threshold means no surcharge applies to this order.
            if (appliedSurchargeCents === 0) set('_surcharges', '');
            set('_b2b_attention', shippingAddress.attention || '');
            // checkout-ui-resideo's DeliveryAddressReviewMessage reads the unprefixed _attention
            // (not _b2b_attention) to render the "Attn:" line on the draft-order invoice checkout.
            set('_attention', shippingAddress.attention || '');
            set('_shipping_address_type', shippingAddress.type || 'current');
            set('_b2b_shipping_instructions', shippingMethod.instructions || '');
            set('_b2b_packing_list_notes', shippingMethod.packing_list_notes || '');
            // checkout-ui-resideo's Checkout.jsx (shipping instructions/packing notes input block)
            // and Thankyou.jsx both read these unprefixed keys, not the _b2b_-prefixed ones above.
            set('shipping_instructions', shippingMethod.instructions || '');
            set('packing_list_notes', shippingMethod.packing_list_notes || '');
            set('_shipping_method_type', shippingMethod.type || 'best_way');
            set('_shipping_carrier', shippingMethod.carrier_external_id || '');
            set('_shipping_service', shippingMethod.service || '');
            set('_shipping_account', shippingMethod.account_number || '');
            set('_shipping_method_display', shippingMethodDisplay);
            // Richer method label for ShippingMethodReviewMessage/DeliveryAddressReviewMessage —
            // falls back to deriving from _shipping_method_type/_shipping_carrier/_shipping_service
            // when these are blank, so this is an enhancement rather than a hard requirement.
            set('_shipping_carrier_description', shippingMethod.carrier_description || '');
            set('_shipping_condition_description', shippingMethod.shipping_condition_description || '');
            set('_ship_complete', shipComplete ? 'true' : 'false');
            set('_billing_same_as_shipping', checkoutData.billing_same_as_shipping ? 'true' : 'false');
            set('_promo_code', discountCodesRaw);
            // Standard checkout's ShippingMethodReviewMessage extension renders this as the "Terms"
            // line under Shipping Address/Method; without it here, draft-order checkout has nothing
            // to display even though the customer selected freight terms on the b2b-checkout page.
            set('_freight_term', shippingMethod.freight_term || '');
            set('_shipping_freight_term_description', shippingMethod.freight_term_description || '');
            set('_checkout_shipping_method_freight_term_description', shippingMethod.freight_term_description || '');
            // Same extension also renders the Carrier Account # under Shipping Method when billing
            // to a carrier account; mirror the attribute names b2b-checkout.js sets on the standard
            // checkout cart (_customer_carrier_account_number / _checkout_shipping_method_account_number).
            set('_shipping_account_number', shippingMethod.account_number || '');
            set('_customer_carrier_account_number', shippingMethod.account_number || '');
            set('_checkout_shipping_method_account_number', shippingMethod.account_number || '');
            set('_sales_area', cartAttributes._sales_area || document.body?.dataset?.currentSalesArea || '');
            // Payment terms code (e.g. C000) resolved from the company's sales_area_data metafield
            // for the current sales area. Set on the cart by cart-updates.js; the hidden input is
            // the same value set asynchronously by rs-current-company-location.js.
            // set('payment_terms', cartAttributes.payment_terms || document.querySelector('.company-payment-terms')?.value || '');
            return Object.entries(attrs).map(([key, value]) => ({key, value}));
          })(),
        };

        if (useCompanyLocationAddress) {
          requestBody.useCompanyLocationAddress = true;
        } else {
          requestBody.shippingAddress = {
            firstName: shippingAddress.firstName || '',
            lastName: shippingAddress.lastName || '',
            address1: shippingAddress.address1 || '',
            address2: shippingAddress.address2 || '',
            city: shippingAddress.city || '',
            provinceCode: shippingAddress.state || '',
            countryCode: shippingAddress.country || '',
            zip: shippingAddress.zip || '',
            phone: shippingAddress.phone || '',
            company: shippingAddress.company || '',
          };
        }

        // The PO number is captured on the order via the `_po_number` custom attribute only.
        // The native Shopify draft-order `poNumber` field is intentionally NOT sent — Shopify
        // renders it into the "Customer Additional Note" area on the payment-only checkout,
        // which we want to keep empty. The cart may already carry a (stale) _po_number —
        // replace it rather than push a duplicate key.
        const replaceAttribute = (key, value) => {
          const idx = requestBody.customAttributes.findIndex((a) => a.key === key);
          if (idx >= 0) requestBody.customAttributes[idx] = {key, value};
          else requestBody.customAttributes.push({key, value});
        };
        if (poNumber) {
          replaceAttribute('_po_number', poNumber);
        } else {
          // Drop any stale _po_number carried over from the cart so the draft order
          // matches the previous behavior (key present only when a PO number is set).
          const stale = requestBody.customAttributes.findIndex((a) => a.key === '_po_number');
          if (stale >= 0) requestBody.customAttributes.splice(stale, 1);
        }

        // Add requested delivery date if set
        if (shippingMethod.requested_delivery_date) {
          replaceAttribute('_requested_delivery_date', shippingMethod.requested_delivery_date);
        } else {
          const staleDate = requestBody.customAttributes.findIndex((a) => a.key === '_requested_delivery_date');
          if (staleDate >= 0) requestBody.customAttributes.splice(staleDate, 1);
        }

        // Add B2B context for Draft Order (purchasingEntity) to maintain B2B checkout experience
        if (this.b2bContext) {
          requestBody.companyId = this.b2bContext.companyId;
          requestBody.companyLocationId = this.b2bContext.locationId;
          requestBody.companyContactId = this.b2bContext.contactId;
        }

        // Create vs update: pass draftOrderId when updating so backend can update existing draft.
        // Backend must accept optional draftOrderId and return draftOrderId + checkoutUrl in response.
        if (isUpdate && sessionState.draftOrderId) {
          requestBody.draftOrderId = sessionState.draftOrderId;
        }

        // Add billing address if different from shipping
        if (!checkoutData.billing_same_as_shipping && billingAddress?.address1) {
          requestBody.billingAddress = {
            firstName: billingAddress.firstName || '',
            lastName: billingAddress.lastName || '',
            address1: billingAddress.address1 || '',
            address2: billingAddress.address2 || '',
            city: billingAddress.city || '',
            provinceCode: billingAddress.state || '',
            countryCode: billingAddress.country || '',
            zip: billingAddress.zip || '',
            phone: billingAddress.phone || '',
            company: billingAddress.company || '',
          };
        }

        const response = await fetch(`${config.gadgetApiBase}/api/checkout/checkout-with-billing`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${window.myJWT}`,
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (data.success && data.checkoutUrl) {
          const cartCurrency = this.cart?.currency || config.currency || '';
          this.setDraftOrderSessionState({
            draftOrderId: data.draftOrderId != null ? String(data.draftOrderId) : isUpdate ? sessionState.draftOrderId : null,
            checkoutUrl: data.checkoutUrl,
            contextHash: currentContextHash,
            cartCurrency: cartCurrency,
            cartFingerprint: currentCartFingerprint,
          });
          return {checkoutUrl: data.checkoutUrl};
        }
        return {error: data?.error || null};
      } catch (error) {
        return {error: error?.message || null};
      }
    },

    /**
     * Cache DOM elements
     */
    cacheElements() {
      this.elements = {
        // Header
        timestamp: document.getElementById('b2b-review-timestamp'),

        // Info Cards
        shipCompleteValue: document.getElementById('b2b-ship-complete-value'),
        shipDateRow: document.getElementById('b2b-ship-date-row'),
        shipDateValue: document.getElementById('b2b-ship-date-value'),
        shippingAddress: document.getElementById('b2b-shipping-address'),
        shippingInstructions: document.getElementById('b2b-shipping-notes'),
        packingNotes: document.getElementById('b2b-packing-notes'),
        paymentMethod: document.getElementById('b2b-payment-method'),
        poNumber: document.getElementById('b2b-po-number'),
        billingAddress: document.getElementById('b2b-billing-address'),

        // Table
        body: document.getElementById('b2b-review-body'),

        // Pagination (top)
        paginationRangeTop: document.getElementById('b2b-pagination-range-top'),
        paginationTotalTop: document.getElementById('b2b-pagination-total-top'),
        prevPageTop: document.getElementById('b2b-prev-page-top'),
        nextPageTop: document.getElementById('b2b-next-page-top'),

        // Pagination (bottom)
        paginationRangeBottom: document.getElementById('b2b-pagination-range-bottom'),
        paginationTotalBottom: document.getElementById('b2b-pagination-total-bottom'),
        prevPageBottom: document.getElementById('b2b-prev-page-bottom'),
        nextPageBottom: document.getElementById('b2b-next-page-bottom'),

        // Rows per page
        rowsPerPageSelect: document.getElementById('b2b-rows-per-page'),
        rowsPerPageSelectBottom: document.getElementById('b2b-rows-per-page-bottom'),

        // Summary
        skuCount: document.getElementById('b2b-sku-count'),
        skuLabel: document.getElementById('b2b-sku-label'),
        subtotal: document.getElementById('b2b-subtotal'),
        shipping: document.getElementById('b2b-shipping'),
        tax: document.getElementById('b2b-tax'),
        total: document.getElementById('b2b-total'),
        totalCurrency: document.getElementById('b2b-total-currency'),
        mobileSummaryTotal: document.getElementById('b2b-mobile-summary-total'),

        // Actions
        submitBtn: document.getElementById('b2b-submit-btn'),
      };
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Pagination buttons
      [this.elements.prevPageTop, this.elements.prevPageBottom].forEach((btn) => {
        if (btn) btn.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      });

      [this.elements.nextPageTop, this.elements.nextPageBottom].forEach((btn) => {
        if (btn) btn.addEventListener('click', () => this.goToPage(this.currentPage + 1));
      });

      // Rows per page selects
      [this.elements.rowsPerPageSelect, this.elements.rowsPerPageSelectBottom].forEach((select) => {
        if (select) {
          select.addEventListener('change', (e) => {
            this.rowsPerPage = parseInt(e.target.value, 10);
            this.currentPage = 1;
            // Sync both selects
            if (this.elements.rowsPerPageSelect) this.elements.rowsPerPageSelect.value = this.rowsPerPage;
            if (this.elements.rowsPerPageSelectBottom) this.elements.rowsPerPageSelectBottom.value = this.rowsPerPage;
            this.render();
          });
        }
      });

      // Submit button — order-simulate.js also binds a click listener on #b2b-submit-btn, so
      // both fire on every click. The isSubmitting guard in submitOrder() makes the duplicate
      // invocation a no-op; keep this binding idempotent too in case init() ever runs twice.
      if (this.elements.submitBtn && !this.elements.submitBtn.dataset.b2bSubmitBound) {
        this.elements.submitBtn.dataset.b2bSubmitBound = 'true';
        this.elements.submitBtn.addEventListener('click', () => this.submitOrder());
      }

      // Re-render rows once RTP populates availability data
      document.addEventListener(
        'cart:availability-updated',
        () => {
          this.render();
          this.updateSummary();
          this.populateInfoCards();
        },
        {once: true},
      );
    },

    /**
     * Set the current timestamp
     */
    setTimestamp() {
      if (this.elements.timestamp) {
        const now = new Date();
        const options = {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZoneName: 'short',
        };
        const formatted = now.toLocaleString('en-US', options);
        this.elements.timestamp.textContent = formatted;
      }
    },

    cartHasZeroPriceItems(cart = this.cart) {
      if (typeof window.cartHasZeroPriceItems === 'function') {
        return window.cartHasZeroPriceItems(cart);
      }
      return (Array.isArray(cart?.items) ? cart.items : []).some((item) => (item.final_price ?? item.price ?? 0) === 0);
    },

    /**
     * Fetch cart data
     */
    async fetchCart() {
      this.renderLoading();
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Failed to fetch cart');
        this.cart = await response.json();
        if (this.cartHasZeroPriceItems()) {
          const config = window.B2B_REVIEW_CONFIG || {};
          window.location.href = config.cartUrl || '/cart';
          return;
        }
        try {
          this.populateInfoCards();
        } catch (error) {
          // Info cards must not block line-item render or flash the empty-cart state.
        }
        this.render();
        this.updateSummary();
      } catch (error) {
        if (this.cart && Array.isArray(this.cart.items) && this.cart.items.length === 0) {
          this.renderEmpty();
        } else {
          this.renderLoading();
        }
      }
    },

    /**
     * Populate info cards with checkout data from sessionStorage or cart attributes
     */
    populateInfoCards() {
      if (!this.cart) return;

      // Prefer checkout data from sessionStorage, fall back to cart attributes
      const checkoutData = this.checkoutData || {};
      const attributes = this.cart.attributes || {};

      // Ship Order Complete
      if (this.elements.shipCompleteValue) {
        const shipComplete = checkoutData.shipping_method?.ship_complete || attributes.ship_complete === 'true';
        this.elements.shipCompleteValue.textContent = shipComplete ? 'Yes' : 'No';
      }

      // Estimated Ship Date (only visible when Ship Order Complete is selected)
      if (this.elements.shipDateValue) {
        const shipComplete = checkoutData.shipping_method?.ship_complete || attributes.ship_complete === 'true';
        if (shipComplete && this.elements.shipDateRow) {
          this.elements.shipDateRow.style.display = '';
          const shipDate = this.getEstimatedShipDate();
          this.elements.shipDateValue.textContent = shipDate;
        } else {
          if (this.elements.shipDateRow) {
            this.elements.shipDateRow.style.display = 'none';
          }
        }
      }

      // Shipping Address (from checkout form data or cart attributes saved by b2b-checkout.js)
      if (this.elements.shippingAddress) {
        const shippingData = checkoutData.shipping_address;
        let addressHtml = '';

        if (shippingData && (shippingData.type === 'new' || shippingData.type === 'addressbook')) {
          // New or address-book address from checkout form
          const name = [shippingData.firstName, shippingData.lastName].filter(Boolean).join(' ') || shippingData.name || '';
          const attentionLine = shippingData.attention ? `${shippingData.attention}<br>` : '';
          addressHtml =
            attentionLine +
            this.formatAddress({
              name: name,
              company: shippingData.company || shippingData.label || '',
              address1: shippingData.address1,
              address2: shippingData.address2,
              city: shippingData.city,
              state: shippingData.state,
              zip: shippingData.zip,
              country: shippingData.country,
            });
        } else if (shippingData && (shippingData.company || shippingData.address_display)) {
          // Current B2B address from sessionStorage
          const attentionLine = shippingData.attention ? `${shippingData.attention}<br>` : '';
          addressHtml = `${shippingData.company || ''}<br>${attentionLine}${shippingData.address_display || ''}`;
        } else {
          // Fallback: build from cart attributes saved by b2b-checkout.js (_shipping_*)
          addressHtml = this.getShippingAddressFromCartAttributes(attributes) || '';
        }

        // Get shipping method
        const shippingMethodData = checkoutData.shipping_method || {};
        const carrier = shippingMethodData.carrier || attributes.shipping_method?.split(/\s+/)[0] || '';
        const method = shippingMethodData.method || attributes.shipping_method?.split(/\s+/).slice(1).join(' ') || '';
        const shippingMethod = [carrier, method].filter(Boolean).join(' ') || 'Best Way';

        const shippingMethodLabel = this.strings.shipping_method || 'Shipping Method';
        this.elements.shippingAddress.innerHTML = `
          <p>${addressHtml}</p>
          <div class="b2b-review__card-content-row">
            <span class="b2b-review__card-label b2b-review__card-label--bold">${shippingMethodLabel}</span>
            <span class="b2b-review__card-value b2b-review__card-value--${shippingMethod.toLowerCase().replace(/\s+/g, '-')}">${shippingMethod}</span>
          </div>
        `;
      }

      // Shipping Instructions
      if (this.elements.shippingInstructions) {
        const noInstructions = this.strings.no_special_instructions || 'No special instructions provided.';
        const instructions = checkoutData.shipping_method?.instructions || attributes._b2b_shipping_instructions || noInstructions;
        this.elements.shippingInstructions.innerHTML = `<p>${instructions || noInstructions}</p>`;
      }

      // Packing Notes - from first cart line item that has _packing_list_notes
      if (this.elements.packingNotes) {
        const packingNotes = checkoutData.shipping_method?.packing_list_notes || 'No packing notes provided.';
        this.elements.packingNotes.textContent = packingNotes;
      }

      // Payment Method, PO Number, and Billing Address
      // These are nested in one card, so we rebuild the entire content
      if (this.elements.paymentMethod) {
        const paymentData = checkoutData.payment_method || {};

        // Get PO number
        const poNumber = paymentData.po_number || attributes.po_number || '';

        // Billing address is always the current company location's address, regardless of the
        // shipping address selected at checkout
        const billingHtml = this.getLocationBillingAddress() || attributes.billing_address || '';

        const s = this.strings;
        this.elements.paymentMethod.innerHTML = `
          <span class="b2b-review__card-label">${s.po_number || 'PO Number:'}</span>
          <span class="b2b-review__card-value" id="b2b-po-number">${this.escapeHtml(poNumber)}</span>
          <span class="b2b-review__card-label b2b-review__card-label--bold rs-mg-t-20">${s.billing_address || 'Billing Address'}</span>
          <span class="b2b-review__card-value" id="b2b-billing-address">${billingHtml}</span>
        `;
      }
    },

    /**
     * Resolve ISO country code (or full name) to a display name for address cards.
     * Returns empty string when country is missing — no static fallback.
     */
    getCountryDisplayName(country) {
      if (!country) return '';
      const value = String(country).trim();
      if (!value) return '';
      if (value.length > 2) return value;
      try {
        const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(value.toUpperCase());
        return name || value;
      } catch (e) {
        return value;
      }
    },

    /**
     * Format address object to HTML string
     */
    formatAddress(address) {
      const parts = [];
      if (address.company) parts.push(address.company);
      if (address.name) parts.push(address.name);
      if (address.address1) parts.push(address.address1);
      if (address.address2) parts.push(address.address2);

      const cityStateZip = [];
      if (address.city) cityStateZip.push(address.city);
      if (address.state) cityStateZip.push(address.state);
      if (address.zip) cityStateZip.push(address.zip);
      if (cityStateZip.length) parts.push(cityStateZip.join(', '));

      const countryName = this.getCountryDisplayName(address.country);
      if (countryName) parts.push(countryName);
      return parts.join('<br>');
    },

    /**
     * Build billing address HTML from the current company location (data-location-* attributes
     * set on <body> in layout/theme.liquid). Returns empty string when no location is set.
     */
    getLocationBillingAddress() {
      const body = document.body;
      const billingAddress = window.companyMetafieldsData?.bill_to_accounts?.[0]?.address;
      if (!billingAddress) return '';
      return this.formatAddress({
        company: body.getAttribute('data-customer-name') || '',
        address1: billingAddress.address_line1,
        address2: billingAddress.address_line2,
        city: billingAddress.city,
        state: billingAddress.province_code,
        zip: billingAddress.postal_code,
        country: billingAddress.country_code,
      });
    },

    /**
     * Build shipping address HTML from cart attributes (saved by b2b-checkout.js saveCartAttributes)
     */
    getShippingAddressFromCartAttributes(attributes) {
      const a = attributes || {};
      const company = a._shipping_company || '';
      const address1 = a._shipping_address1 || '';
      const address2 = a._shipping_address2 || '';
      const city = a._shipping_city || '';
      const state = a._shipping_state || '';
      const zip = a._shipping_zip || '';
      const firstName = a._shipping_first_name || '';
      const lastName = a._shipping_last_name || '';
      const name = [firstName, lastName].filter(Boolean).join(' ');
      let country = a._shipping_country || '';
      if (!country && a._checkout_custom_shipping_address_json) {
        try {
          const parsed = JSON.parse(a._checkout_custom_shipping_address_json);
          country = parsed?.country || '';
        } catch (e) {
          // ignore invalid JSON
        }
      }
      if (!address1 && !city && !zip) return '';
      return this.formatAddress({
        name: name,
        company: company,
        address1: address1,
        address2: address2,
        city: city,
        state: state,
        zip: zip,
        country: country,
      });
    },

    /**
     * Get estimated ship date
     */
    getEstimatedShipDate() {
      const stored = sessionStorage.getItem('rs_ship_complete_date');
      if (stored) {
        const d = new Date(stored);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'});
        }
      }
    },

    /**
     * Expand cart items into display rows, splitting items with _requested_deliveries into one row per split.
     * @returns {Array<{item: any, splitQty: number|null, splitDate: string|null}>}
     */
    expandCartItems() {
      const cart = /** @type {any} */ (this.cart);
      const rows = [];
      for (const item of cart?.items || []) {
        // `_requested_deliveries` holds every split (date + quantity), primary first, and its
        // leading date mirrors `_delivery_date`. Carts saved earlier hold splits 2+ only.
        const firstDate = item.properties?._delivery_date || item.properties?._requested_delivery || '';
        let deliveries = [];
        try {
          const raw = item.properties?._requested_deliveries;
          let parsed = raw;
          if (typeof raw === 'string') {
            parsed = JSON.parse(raw);
          }
          if (parsed && Array.isArray(parsed)) {
            deliveries = parsed.flat(2).map((d) => {
              if (!d || typeof d !== 'object') return null;
              const quantity = d.quantity ?? d.q;
              const date = d._delivery_date ?? d.d;
              if (!quantity && !date) return null;
              return {
                ...d,
                quantity,
                _delivery_date: date,
              };
            }).filter(Boolean);
          }
        } catch {
          /* ignore */
        }

        const includesPrimary =
          !!firstDate &&
          deliveries.length > 0 &&
          String(deliveries[0]._delivery_date || '').trim() === String(firstDate).trim();
        const extraDeliveries = includesPrimary ? deliveries.slice(1) : deliveries;

        if (firstDate || extraDeliveries.length > 0) {
          // Shopify line item `quantity` is the total across all splits (set by createDeliveryDateLineItem).
          const extraQtySum = extraDeliveries.reduce((sum, d) => {
            const q = Number(d.quantity);
            return sum + (Number.isFinite(q) && q > 0 ? q : 0);
          }, 0);
          const storedPrimaryQty = includesPrimary ? Number(deliveries[0].quantity) : NaN;
          const primaryQty =
            Number.isFinite(storedPrimaryQty) && storedPrimaryQty > 0
              ? storedPrimaryQty
              : Math.max(0, (Number(item.quantity) || 0) - extraQtySum);
          if (firstDate) {
            rows.push({
              item,
              splitQty: primaryQty > 0 ? primaryQty : item.quantity,
              splitDate: firstDate,
              splitNotes: item.properties?._packing_list_notes || null,
            });
          }
          for (const d of extraDeliveries) {
            rows.push({item, splitQty: Number(d.quantity) || null, splitDate: d._delivery_date || null, splitNotes: d._packing_list_notes || null});
          }
        } else {
          rows.push({item, splitQty: null, splitDate: null, splitNotes: item.properties?._packing_list_notes});
        }
      }
      return rows;
    },

    /**
     * Render the table
     */
    render() {
      if (!this.elements.body) return;

      if (!this.cart) {
        this.renderLoading();
        return;
      }

      if (!this.cart.items || this.cart.items.length === 0) {
        this.renderEmpty();
        return;
      }

      const displayRows = this.expandCartItems();

      // Calculate pagination over expanded rows
      const totalItems = displayRows.length;
      const totalPages = Math.ceil(totalItems / this.rowsPerPage);
      const startIndex = (this.currentPage - 1) * this.rowsPerPage;
      const endIndex = Math.min(startIndex + this.rowsPerPage, totalItems);
      const pageItems = displayRows.slice(startIndex, endIndex);

      // Render rows
      this.elements.body.innerHTML = pageItems.map(({item, splitQty, splitDate, splitNotes}) => this.renderRow(item, splitQty, splitDate, splitNotes)).join('');

      // Update pagination
      this.updatePagination(startIndex, endIndex, totalItems, totalPages);
    },

    /**
     * Primary image from product.metafields.sku.primary_image (injected via Liquid).
     */
    getPrimaryImageUrlForItem(item) {
      const list = window.B2B_REVIEW_ITEMS_PRIMARY_IMAGES;
      if (!Array.isArray(list)) return null;
      const entry = list.find((e) => e && Number(e.variant_id) === Number(item.variant_id));
      if (!entry) return null;
      if (entry.file_path && entry.file_path !== '') return entry.file_path;
      if (entry.shopify_cdn_url && entry.shopify_cdn_url !== '') return entry.shopify_cdn_url;
      return null;
    },

    /**
     * Resideo material number from product.metafields.product.number (injected via Liquid).
     * Same source as the cart SKU column, so Review Order matches Cart.
     */
    getProductNumberForItem(item) {
      const list = window.B2B_REVIEW_ITEMS_PRODUCT_NUMBERS;
      if (!Array.isArray(list)) return '';
      const entry = list.find((e) => e && Number(e.variant_id) === Number(item.variant_id));
      return entry?.product_number ? String(entry.product_number).trim() : '';
    },

    /**
     * Sales area external_id from context cookie (same as cart / b2b-checkout).
     * @returns {string}
     */
    getSalesAreaExternalId() {
      if (typeof window.getSalesAreaExternalId === 'function') {
        return String(window.getSalesAreaExternalId() || '').trim();
      }
      try {
        const cookies = (document.cookie || '').split(';').reduce((acc, s) => {
          const i = s.indexOf('=');
          if (i === -1) return acc;
          acc[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(s.slice(i + 1).trim());
          return acc;
        }, /** @type {Record<string, string>} */ ({}));
        const ctx = cookies.context ? JSON.parse(decodeURIComponent(cookies.context)) : null;
        if (!ctx) return String(document.body?.dataset?.currentSalesArea || '').trim();
        const cand = ctx.sales_area;
        const saRaw = Array.isArray(cand) ? cand[0]?.fields || cand[0] : cand?.fields || cand || {};
        return String(saRaw?.external_id ?? saRaw?.sales_area_id ?? document.body?.dataset?.currentSalesArea ?? '').trim();
      } catch {
        return String(document.body?.dataset?.currentSalesArea || '').trim();
      }
    },

    /**
     * @param {any} item
     * @returns {string}
     */
    salesAreaIdFromRow(item) {
      const sa = item?.sales_area;
      if (sa == null) return '';
      if (typeof sa === 'object') {
        return String(sa.external_id?.value ?? sa.external_id ?? sa.id ?? '').trim();
      }
      return String(sa).trim();
    },

    /**
     * @param {any} salesData
     * @param {string} externalId
     * @returns {any | null}
     */
    resolveCompanySalesData(salesData, externalId) {
      if (typeof window.resolveCompanySalesData === 'function') {
        return window.resolveCompanySalesData(salesData, externalId);
      }
      if (!salesData) return null;
      let parsed = salesData;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          return null;
        }
      }
      const ext = String(externalId || '').trim();
      if (Array.isArray(parsed)) {
        if (ext) {
          const match = parsed.find(
            (row) => row && this.salesAreaIdFromRow(row).toLowerCase() === ext.toLowerCase()
          );
          if (match) return match;
        }
        return parsed[0] || null;
      }
      if (parsed && typeof parsed === 'object') {
        if (parsed.value != null) return this.resolveCompanySalesData(parsed.value, ext);
        return parsed;
      }
      return null;
    },

    /**
     * @param {any} row
     * @returns {string}
     */
    plantNameFromSalesRow(row) {
      if (typeof window.plantNameFromSalesRow === 'function') {
        return window.plantNameFromSalesRow(row) || '';
      }
      if (!row || typeof row !== 'object') return '';
      let sourcePlant = row.source_plant;
      if (sourcePlant && typeof sourcePlant === 'object' && sourcePlant.value != null) {
        sourcePlant = sourcePlant.value;
      }
      if (!sourcePlant) return '';
      if (typeof sourcePlant === 'string' && sourcePlant.trim()) return sourcePlant.trim();
      if (typeof sourcePlant === 'object') {
        const name = sourcePlant.name?.value ?? sourcePlant.name ?? sourcePlant.title?.value ?? sourcePlant.title;
        if (name != null && String(name).trim()) return String(name).trim();
      }
      return '';
    },

    /**
     * Resolved sales_data row for a cart line (same source as cart Service Class / plant).
     * @param {any} item
     * @returns {any | null}
     */
    getSalesDataForItem(item) {
      const list = window.B2B_REVIEW_ITEMS_SALES_DATA;
      if (!Array.isArray(list) || !item) return null;

      const entry =
        list.find((e) => e && item.key && e.key === item.key) ||
        list.find((e) => e && Number(e.variant_id) === Number(item.variant_id));
      if (!entry?.sales_data || (Array.isArray(entry.sales_data) && entry.sales_data.length === 0)) {
        return null;
      }

      return this.resolveCompanySalesData(entry.sales_data, this.getSalesAreaExternalId());
    },

    /**
     * Plant name from line properties, or product sales_data (same source as cart availability).
     * @param {any} item
     * @returns {string}
     */
    getPlantForItem(item) {
      const fromProps = item?.properties?.plant || item?.properties?._plant || '';
      if (fromProps) return String(fromProps);

      return this.plantNameFromSalesRow(this.getSalesDataForItem(item)) || '';
    },

    /**
     * Unwrap a sales_data field that may be a scalar or `{ value }`.
     * @param {any} value
     * @returns {any}
     */
    salesFieldValue(value) {
      if (value && typeof value === 'object' && value.value != null) return value.value;
      return value;
    },

    /**
     * Service Class + Min Qty | Multiple — same source and copy as cart/PLP.
     * Omits a line when that field is missing (no hardcoded 3-day / 1|1 fallback).
     * @param {any} item
     * @returns {string}
     */
    renderSalesMeta(item) {
      const t = typeof window.__t === 'function' ? window.__t : null;
      if (!t) return '';

      const sd = this.getSalesDataForItem(item);
      const days = this.salesFieldValue(sd?.serv_lvl_days);
      const minQty = this.salesFieldValue(sd?.minimum_order_quantity);
      const multiple = this.salesFieldValue(sd?.ship_increment_quantity);
      const parts = [];

      if (days != null && String(days).trim() !== '') {
        parts.push(
          `${t('service_class', {days: this.escapeHtml(String(days))})} <span class="rs-mob-hidden">${t('business_days')}</span><span class="rs-desktop-hidden">${t('biz_days')}</span>`
        );
      }

      const hasMin = minQty != null && minQty !== '';
      const hasMult = multiple != null && multiple !== '';
      if (hasMin || hasMult) {
        const minStr = hasMin ? this.escapeHtml(String(minQty == 0 ? 1 : minQty)) : '—';
        const multStr = hasMult ? this.escapeHtml(String(multiple)) : '—';
        parts.push(`${t('min_qty', {count: minStr})} | ${t('multiple', {count: multStr})}`);
      }

      return parts.join('<br>');
    },

    /**
     * Render a single display row.
     * When called from expandCartItems for a split delivery, splitQty and splitDate override
     * the item-level quantity and delivery date for that specific split.
     * @param {any} item
     * @param {number | null} splitQty
     * @param {string | null} splitDate
     * @param {any} splitNotes
     */
    renderRow(item, splitQty, splitDate, splitNotes) {
      const primaryImageUrl = this.getPrimaryImageUrlForItem(item);
      const imageUrl = primaryImageUrl || item.featured_image?.url || item.image;
      const sku = (item.sku || this.extractSku(item)).trim();
      const title = item.title;
      const productNumber = this.getProductNumberForItem(item) || sku;
      const unitPrice = this.getRtpUnitCents(item) ?? item.price;

      // For split rows use the split qty; otherwise the full item qty
      const displayQty = splitQty != null ? splitQty : item.quantity;
      const lineTotal = unitPrice * displayQty;

      const plant = this.getPlantForItem(item);
      const salesMeta = this.renderSalesMeta(item);
      const s = /** @type {any} */ (this).strings || {};
      const plantLabel = s.plant || 'Plant:';
      const unitsShipOn = s.units_will_ship_on_prefix || 'units will ship on';

      // For a split row the availability is just this split's qty + date
      let availabilityBlocks = [];
      if (splitQty != null && splitDate) {
        availabilityBlocks = [{quantity: splitQty, _delivery_date: splitDate}];
      } else {
        // No split — try ATP data, then fallback to single block with 7-day ship estimate
        const shipDate = this.getShipDate(7);
        try {
          const schedule = item.properties?.availability_schedule;
          if (schedule) {
            const parsed = typeof schedule === 'string' ? JSON.parse(schedule) : schedule;
            availabilityBlocks = Array.isArray(parsed) ? parsed : parsed?.quantity && (parsed?._delivery_date || parsed?.date) ? [parsed] : [];
          }
        } catch {
          /* ignore */
        }

        if (availabilityBlocks.length === 0) {
          const atpList = window.RTP?.lastCartMaterialList;
          const aliases = window.RTP?.pricingSkuAliases;
          let atpItem = null;
          if (Array.isArray(atpList)) {
            atpItem = atpList.find((m) => {
              const matNum = String(m.materialNumber ?? '').trim();
              if (matNum === sku) return true;
              const raws = aliases?.get(matNum);
              if (raws && raws.some((r) => String(r).trim() === sku)) return true;
              return false;
            });
          }
          const atpEntries = (atpItem?.availability || []).filter((a) => parseInt(a.availableQty, 10) > 0);
          if (atpEntries.length) {
            availabilityBlocks = atpEntries.map((a) => ({quantity: parseInt(a.availableQty, 10), _delivery_date: a.availableDate}));
          } else {
            availabilityBlocks = [{quantity: item.quantity, _delivery_date: shipDate}];
          }
        }
      }

      // Req delivery: mirror cart — only show dates stored on the line item (never auto-fill a default).
      let requestedDelivery = '';
      if (splitQty != null) {
        requestedDelivery = splitDate || '';
      } else {
        requestedDelivery = item.properties?._delivery_date || item.properties?._requested_delivery || '';
      }
      if (!requestedDelivery) {
        requestedDelivery = '-';
      }

      const shipComplete = item.properties?._ship_complete === 'true';
      const shipCompleteLabel = s.ship_complete || 'Ship Complete:';
      const shipCompleteValue = shipComplete ? s.yes || 'Yes' : s.no || 'No';
      const shipCompleteDate = shipComplete ? item.properties?._ship_complete_date || '' : '';

      const noImageText = s.no_image_available_full || s.no_image_available || 'No Image Available';

      const availabilityHtml = availabilityBlocks
        .map((block) => {
          const qty = block.quantity || block.availableQty || displayQty;
          const date = shipCompleteDate || block._delivery_date || block.availableDate || block.date || this.getShipDate(7);
          return `<div class="b2b-review__ship-info">${qty} ${unitsShipOn} <strong>${date}</strong></div>`;
        })
        .join('');

      return `
        <div class="b2b-review__row">
          <div class="b2b-review__sku-cell">
            <div class="b2b-review__product-image">
              ${imageUrl ? `<img src="${primaryImageUrl ? this.escapeHtml(imageUrl) : this.resizeImage(imageUrl, '120x120')}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">` : noImageText}
            </div>
            <div class="b2b-review__sku-info">
              <a href="${item.url}" class="b2b-review__sku-link">${this.escapeHtml(productNumber || title)}</a>
              ${salesMeta ? `<div class="b2b-review__sku-meta">${salesMeta}</div>` : ''}
            </div>
          </div>

          <div class="b2b-review__qty-cell" data-label="Qty">
            ${displayQty}
          </div>

          <div class="b2b-review__availability-cell" data-label="Availability">
            <div class="b2b-review__plant"><strong>${plantLabel}</strong> ${plant ? this.escapeHtml(plant) : '—'}</div>
            ${availabilityHtml}
            <div class="b2b-review__ship-complete-row">${shipCompleteLabel} ${shipCompleteValue}</div>
          </div>

          <div class="b2b-review__delivery-cell" data-label="Req Delivery">
            ${requestedDelivery}
          </div>

          <div class="b2b-review__price-cell" data-label="Price">
            <div class="b2b-review__unit-price"><strong>${this.formatMoney(unitPrice)}</strong> each</div>
            <div class="b2b-review__line-total">Total <strong>${this.formatMoney(lineTotal)}</strong></div>
          </div>

          ${
            splitNotes
              ? `
          <div class="b2b-review__packing-list-notes-cell" data-label="Packing List Notes">
              <div class="b2b-review__packing-list-notes-title"><strong>${s.packing_list_notes || 'Packing List Notes:'}:</strong></div>
              <div class="b2b-review__packing-list-notes-content">${splitNotes || ''}</div>
            </div>
          `
              : ''
          }
        </div>
      `;
    },

    /**
     * Render loading state while cart items are being fetched or painted.
     */
    renderLoading() {
      if (!this.elements.body) return;
      const s = this.strings || {};
      const label = s.loading_order_details || 'Loading order details...';

      this.elements.body.innerHTML = `
        <div class="b2b-review__loading">
          <div class="b2b-review__spinner"></div>
          <span>${label}</span>
        </div>
      `;
    },

    /**
     * Render empty state
     */
    renderEmpty() {
      const config = window.B2B_REVIEW_CONFIG || {};
      const s = this.strings;

      this.elements.body.innerHTML = `
        <div class="b2b-review__empty">
          <h2 class="b2b-review__empty-title">${config.emptyTitle || s.empty_cart_title || 'Your cart is empty'}</h2>
          <p class="b2b-review__empty-text">${config.emptyText || s.empty_cart_text || 'Add items to your cart to review your order.'}</p>
          <a href="${config.emptyButtonUrl || '/collections/all'}" class="b2b-review__empty-btn">
            ${config.emptyButtonText || s.browse_products || 'Browse Products'}
          </a>
        </div>
      `;

      // Update summary to zero
      this.updateSummaryZero();
    },

    /**
     * Update pagination controls
     */
    updatePagination(start, end, total, totalPages) {
      // Top pagination
      if (this.elements.paginationRangeTop) {
        this.elements.paginationRangeTop.textContent = `${start + 1}-${end}`;
      }
      if (this.elements.paginationTotalTop) {
        this.elements.paginationTotalTop.textContent = total;
      }
      if (this.elements.prevPageTop) {
        this.elements.prevPageTop.disabled = this.currentPage <= 1;
      }
      if (this.elements.nextPageTop) {
        this.elements.nextPageTop.disabled = this.currentPage >= totalPages;
      }

      // Bottom pagination
      if (this.elements.paginationRangeBottom) {
        this.elements.paginationRangeBottom.textContent = `${start + 1}-${end}`;
      }
      if (this.elements.paginationTotalBottom) {
        this.elements.paginationTotalBottom.textContent = total;
      }
      if (this.elements.prevPageBottom) {
        this.elements.prevPageBottom.disabled = this.currentPage <= 1;
      }
      if (this.elements.nextPageBottom) {
        this.elements.nextPageBottom.disabled = this.currentPage >= totalPages;
      }
    },

    /**
     * Go to a specific page
     */
    goToPage(page) {
      const totalPages = Math.ceil((this.cart?.items?.length || 0) / this.rowsPerPage);
      if (page < 1 || page > totalPages) return;
      this.currentPage = page;
      this.render();
    },

    /**
     * Update order summary
     */
    /**
     * Order subtotal in cents. Sums price × per-split qty so split-delivery rows contribute their
     * correct partial total. item.line_price / cart.total_price can be $0 for items added to cart
     * before RTP ran.
     * @returns {number}
     */
    getSubtotalCents() {
      return this.expandCartItems().reduce((sum, /** @type {any} */ row) => {
        const qty = row.splitQty != null ? row.splitQty : row.item.quantity || 0;
        const unitCents = this.getRtpUnitCents(row.item) ?? row.item.price ?? 0;
        return sum + unitCents * qty;
      }, 0);
    },

    /**
     * Surcharge (cents) that actually applies to this order. The stored `_surcharges` is the raw
     * simulated fee; it only applies to orders under $500. Used for both the summary and what is
     * submitted to the draft order, so the customer is charged what they were shown.
     * @param {number} [subtotalCents]
     * @returns {number}
     */
    getAppliedSurchargeCents(subtotalCents = this.getSubtotalCents()) {
      const surchargesRaw = this.cart?.attributes?._surcharges;
      const surchargeValue = surchargesRaw != null && surchargesRaw !== '' ? parseFloat(surchargesRaw) : 0;
      const SURCHARGE_THRESHOLD_CENTS = 50000;
      if (subtotalCents >= SURCHARGE_THRESHOLD_CENTS) return 0;
      return Number.isFinite(surchargeValue) && surchargeValue >= 0 ? Math.round(surchargeValue * 100) : 0;
    },

    updateSummary() {
      if (!this.cart) return;

      const subtotal = this.getSubtotalCents();
      // SKU count = number of unique Shopify line items, not expanded display rows
      const itemCount = Array.isArray(/** @type {any} */ (this.cart)?.items) ? /** @type {any} */ (this.cart).items.length : 0;
      const tax = 0; // Tax is calculated at checkout
      const shippingCents = this.getAppliedSurchargeCents(subtotal);

      if (this.elements.skuCount) {
        this.elements.skuCount.textContent = String(Math.max(0, Math.floor(itemCount))).replace(/\s/g, '');
      }

      if (this.elements.subtotal) {
        this.elements.subtotal.textContent = this.formatMoney(subtotal);
      }

      if (this.elements.tax) {
        this.elements.tax.textContent = this.formatMoney(tax);
      }

      if (this.elements.shipping) {
        this.elements.shipping.dataset.surchargesCents = String(shippingCents);
        if (shippingCents === 0) {
          // Hide the shipping row when surcharge is $0
          const shippingRow = this.elements.shipping.closest('.b2b-review__summary-row');
          if (shippingRow) shippingRow.style.display = 'none';
        } else {
          const shippingRow = this.elements.shipping.closest('.b2b-review__summary-row');
          if (shippingRow) shippingRow.style.display = '';
          // Update the visible text with the actual surcharge amount
          this.elements.shipping.textContent = this.formatMoney(shippingCents);
        }
      }
      if (this.elements.total) {
        this.elements.total.textContent = this.formatMoney(subtotal + tax + shippingCents);
        if (this.elements.totalCurrency) this.elements.totalCurrency.textContent = this.getRtpCurrency() || '';
      }
      if (this.elements.mobileSummaryTotal) {
        this.elements.mobileSummaryTotal.textContent = this.formatMoney(subtotal + tax + shippingCents);
      }
    },

    /**
     * Update summary to zero values
     */
    updateSummaryZero() {
      const s = this.strings;
      if (this.elements.skuCount) this.elements.skuCount.textContent = '0';
      if (this.elements.skuLabel) this.elements.skuLabel.textContent = s.skus || 'SKUs';
      if (this.elements.subtotal) this.elements.subtotal.textContent = this.formatMoney(0);
      if (this.elements.shipping) {
        this.elements.shipping.textContent = this.formatMoney(0);
        const shippingRow = this.elements.shipping.closest('.b2b-review__summary-row');
        if (shippingRow) shippingRow.style.display = 'none';
      }
      if (this.elements.tax) this.elements.tax.textContent = this.formatMoney(0);
      if (this.elements.total) this.elements.total.textContent = this.formatMoney(0);
      if (this.elements.totalCurrency) this.elements.totalCurrency.textContent = this.getRtpCurrency() || '';
      if (this.elements.mobileSummaryTotal) this.elements.mobileSummaryTotal.textContent = this.formatMoney(0);
    },

    /**
     * Submit the order
     */
    async submitOrder() {
      const s = this.strings;

      // Re-entrancy guard: two click listeners are bound to #b2b-submit-btn (bindEvents here
      // plus order-simulate.js), so one click fires submitOrder twice. Without this guard both
      // calls race past the button-disable below and both POST without a stored draftOrderId —
      // the backend then creates TWO draft orders instead of create + update.
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
        this.isSubmitting = false;
        alert(s.cart_empty_alert || 'Your cart is empty.');
        return;
      }

      // Draft orders never run the Cart Transform, so each line's price must be carried in
      // explicitly. If real-time pricing hasn't resolved a price for a line yet, block submission
      // rather than let that line silently fall back to Shopify's list price on the draft order.
      if (window.B2B_REVIEW_CONFIG?.useDraftOrderCheckout) {
        const unresolvedItem = this.cart.items.find((item) => this.getRtpUnitCents(item) == null);
        if (unresolvedItem) {
          this.isSubmitting = false;
          alert(s.pricing_not_ready_alert || 'Pricing is still updating for one or more items. Please wait a moment and try again.');
          return;
        }
      }

      const originalSubmitBtnText = this.elements.submitBtn?.textContent;
      if (this.elements.submitBtn) {
        this.elements.submitBtn.disabled = true;
        this.elements.submitBtn.textContent = s.processing || 'Processing...';
      }

      try {
        // Build comprehensive cart attributes for Shopify checkout
        let checkoutData = this.checkoutData || {};

        // sessionStorage can be wiped (company/location change in rs-location-picker.js,
        // tab reload) even though the address was already saved to the cart on the checkout
        // page. Fall back to the cart's _checkout_custom_shipping_address_json so the draft
        // order and the order-attribute sync still use the customer's chosen address.
        if (!checkoutData.shipping_address?.address1 && !checkoutData.shipping_address?.city) {
          const restored = this.restoreShippingAddressFromCartAttributes();
          if (restored) {
            checkoutData = { ...checkoutData, shipping_address: restored };
          }
        }

        const paymentMethod = checkoutData.payment_method?.type || 'purchase_order';
        const shippingAddress = checkoutData.shipping_address || {};
        const shippingMethod = checkoutData.shipping_method || {};
        const billingAddress = checkoutData.billing_address;

        // Core attributes for checkout (merge with existing to preserve e.g. _promo_code)
        const existingAttributes = this.cart?.attributes || {};
        const attributes = {
          ...existingAttributes,
          // Payment method for payment customization function
          // PO Number - use both attribute names for compatibility
          _po_number: checkoutData.payment_method?.po_number || '',

          // Shipping address info — the flat keys (not just the JSON attribute) are what the
          // thank-you order sync (/api/orders/sync-order-shipping-address-from-attributes)
          // applies to the order's shippingAddress, so all of them must be populated.
          _shipping_address_type: shippingAddress.type || 'current',
          _attention: shippingAddress.attention || '',
          _shipping_address1: shippingAddress.address1 || '',
          _shipping_address2: shippingAddress.address2 || '',
          _shipping_city: shippingAddress.city || '',
          _shipping_state: shippingAddress.state || '',
          _shipping_zip: shippingAddress.zip || '',
          _shipping_country: shippingAddress.country || '',
          _shipping_company: shippingAddress.company || '',
          _shipping_first_name: shippingAddress.firstName || '',
          _shipping_last_name: shippingAddress.lastName || '',
          _shipping_phone: shippingAddress.phone || '',

          // Shipping address as JSON — only for "new" or "addressbook"; empty for "current"
          ...(shippingAddress.type === 'new' ? {
            _checkout_custom_shipping_address_json: JSON.stringify({
              type: shippingAddress.type,
              company: shippingAddress.company || '',
              attention: shippingAddress.attention || '',
              address1: shippingAddress.address1 || '',
              address2: shippingAddress.address2 || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              zip: shippingAddress.zip || '',
              country: shippingAddress.country || '',
              phone: shippingAddress.phone || '',
              firstName: shippingAddress.firstName || '',
              lastName: shippingAddress.lastName || '',
              save_to_addressbook: shippingAddress.save_to_addressbook || false,
            }),
          } : shippingAddress.type === 'addressbook' ? {
            _checkout_custom_shipping_address_json: JSON.stringify({
              type: shippingAddress.type,
              company: shippingAddress.company || shippingAddress.label || '',
              attention: shippingAddress.attention || '',
              address1: shippingAddress.address1 || '',
              address2: shippingAddress.address2 || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              zip: shippingAddress.zip || '',
              country: shippingAddress.country || '',
              firstName: shippingAddress.firstName || '',
              lastName: shippingAddress.lastName || '',
              ...(shippingAddress.addressbook_id ? {addressbook_id: shippingAddress.addressbook_id} : {}),
            }),
          } : {
            _checkout_custom_shipping_address_json: '',
          }),

          // Shipping method
          _shipping_method_type: shippingMethod.type || 'best_way',
          _shipping_carrier: shippingMethod.carrier_external_id || '',
          _shipping_service: shippingMethod.service || '',
          _shipping_account: shippingMethod.account_number || '',
          _shipping_account_number: shippingMethod.account_number || '',
          _customer_carrier_account_number: shippingMethod.account_number || '',
          _checkout_shipping_method_account_number: shippingMethod.account_number || '',
          _b2b_shipping_instructions: shippingMethod.instructions || '',

          // Billing same as shipping
          _billing_same_as_shipping: checkoutData.billing_same_as_shipping ? 'true' : 'false',
        };

        // If billing is different, add billing address attributes
        if (!checkoutData.billing_same_as_shipping && billingAddress) {
          attributes['_billing_address1'] = billingAddress.address1 || '';
          attributes['_billing_address2'] = billingAddress.address2 || '';
          attributes['_billing_city'] = billingAddress.city || '';
          attributes['_billing_state'] = billingAddress.state || '';
          attributes['_billing_zip'] = billingAddress.zip || '';
          attributes['_billing_country'] = billingAddress.country || '';
        }

        // Single cart update: attributes + optional PO note (avoids back-to-back /cart/update.js rate limits)
        const cartUpdatePayload = {
          attributes,
          ...(checkoutData.payment_method?.po_number ? {note: `PO Number: ${checkoutData.payment_method.po_number}`} : {}),
        };
        const cartUpdateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(cartUpdatePayload),
        });
        if (!cartUpdateResponse.ok) {
          const errText = await cartUpdateResponse.text().catch(() => '');
          console.error('[b2b-review-order] Failed to update cart attributes before order submission:', cartUpdateResponse.status, errText);
        }

        const config = window.B2B_REVIEW_CONFIG || {};

        // When feature flag is on: create or update Draft Order, then redirect to payment-only
        // Shopify Checkout. Never fall back to standard checkout on failure — draft order pricing
        // is not equivalent to standard checkout, so stop and let the customer retry instead.
        if (config.useDraftOrderCheckout) {
          // Re-read the cart so createDraftOrderCheckout seeds its customAttributes from the
          // attributes we just wrote above (e.g. _shipping_address1, _billing_address1,
          // _checkout_custom_shipping_address_json) — this.cart is still the pre-update snapshot.
          await this.fetchCart();
          const draftOrderResult = await this.createDraftOrderCheckout();
          if (draftOrderResult?.checkoutUrl) {
            // Clear draft order session state BEFORE redirecting so that if the customer
            // navigates back or retries, a fresh draft order is created instead of trying to
            // update a potentially stale/completed one — which causes merchandise signature mismatches.
            this.clearDraftOrderSessionKeys();
            window.location.href = draftOrderResult.checkoutUrl;
            return;
          }
          if (draftOrderResult?.error) console.error('[b2b-review-order] Draft order checkout failed:', draftOrderResult.error);
          this.isSubmitting = false;
          if (this.elements.submitBtn) {
            this.elements.submitBtn.disabled = false;
            this.elements.submitBtn.textContent = originalSubmitBtnText || this.elements.submitBtn.textContent;
          }
          alert(s.draft_order_failed_alert || 'We were unable to process your order. Please try again or contact your account representative.');
          return;
        }

        const checkoutUrl = config.checkoutUrl || '/checkout';
        window.location.href = checkoutUrl;
      } catch (error) {
        this.isSubmitting = false;
        if (window.B2B_REVIEW_CONFIG?.useDraftOrderCheckout) {
          if (this.elements.submitBtn) {
            this.elements.submitBtn.disabled = false;
            this.elements.submitBtn.textContent = originalSubmitBtnText || this.elements.submitBtn.textContent;
          }
          alert(s.draft_order_failed_alert || 'We were unable to process your order. Please try again or contact your account representative.');
          return;
        }
        // Still navigate to checkout even if attribute update fails
        const checkoutUrl = (window.B2B_REVIEW_CONFIG && window.B2B_REVIEW_CONFIG.checkoutUrl) || '/checkout';
        window.location.href = checkoutUrl;
      }
    },

    /**
     * Extract SKU from item
     */
    extractSku(item) {
      if (item.handle) {
        return item.handle.toUpperCase().replace(/-/g, '').trim();
      }
      if (item.variant_title && item.variant_title !== 'Default Title') {
        return item.variant_title.trim();
      }
      if (item.variant_id) {
        return `VAR-${item.variant_id}`;
      }
      return '';
    },

    /**
     * Format money
     */
    formatMoney(cents, currency) {
      const code = currency || this.getRtpCurrency() || 'USD';
      return new Intl.NumberFormat(undefined, {style: 'currency', currency: code, currencyDisplay: 'narrowSymbol'}).format(cents / 100);
    },

    getRtpCurrency() {
      const list = window.RTP?.lastCartMaterialList;
      return Array.isArray(list) ? list[0]?.currencyCode || null : null;
    },

    getRtpUnitCents(item) {
      const override = item?.properties?._price_override;
      if (override != null && override !== '') {
        const parsed = Number(override);
        if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed * 100);
      }
      const sku = (item?.sku || this.extractSku(item) || '').trim();
      const list = window.RTP?.lastCartMaterialList;
      const aliases = window.RTP?.pricingSkuAliases;
      if (!Array.isArray(list) || !sku) return null;
      const match = list.find((m) => {
        const matNum = String(m.materialNumber ?? '').trim();
        if (matNum === sku) return true;
        const raws = aliases?.get(matNum);
        return raws ? raws.some((r) => String(r).trim() === sku) : false;
      });
      if (!match || match.netPrice == null) return null;
      return Math.round(Number(match.netPrice) * 100);
    },

    /**
     * Escape HTML
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text || '';
      return div.innerHTML;
    },

    /**
     * Resize Shopify image
     */
    resizeImage(url, size) {
      if (!url) return '';
      return url.replace(/(\.[^.]+)$/, `_${size}$1`);
    },

    /**
     * Get ship date (days from now)
     */
    getShipDate(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'});
    },

  };

  let resolveB2BReviewReady;
  window.B2BReviewReady = new Promise((resolve) => {
    resolveB2BReviewReady = resolve;
  });

  async function bootstrap() {
    try {
      await B2BReview.init();
    } finally {
      resolveB2BReviewReady();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootstrap());
  } else {
    bootstrap();
  }

  // Export for debugging
  window.B2BReview = B2BReview;
})();
